import type { RequirementBlock, RequirementValue } from '../components/ProjectRequirements'

export const REQUIREMENT_FONT_SIZES = [12,14,16,18,20,22,24,28,32,36] as const
export const escapeRequirementHtml = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')
export function sanitizeRichHtml(html: string): string {
  const template = document.createElement('template')
  template.innerHTML = html.slice(0, 200000)
  const allowed = new Set(['B','STRONG','I','EM','U','BR','P','DIV','SPAN','H1','H2','H3','UL','OL','LI','BLOCKQUOTE'])
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_ELEMENT), elements: Element[] = []
  for (let node = walker.nextNode(); node; node = walker.nextNode()) elements.push(node as Element)
  for (const element of elements) {
    if (['SCRIPT','STYLE','IFRAME','OBJECT','SVG','MATH'].includes(element.tagName)) { element.remove(); continue }
    if (!allowed.has(element.tagName)) { element.replaceWith(...Array.from(element.childNodes)); continue }
    // Retain only supported typography; arbitrary pasted CSS and handlers are discarded.
    const style = (element as HTMLElement).style
    const size = /^(\d+)px$/.exec(style?.fontSize ?? '')?.[1]
    const fontSize = size && (REQUIREMENT_FONT_SIZES as readonly number[]).includes(Number(size)) ? Number(size) : null
    const align = /^(P|DIV|H1|H2|H3|LI|BLOCKQUOTE)$/.test(element.tagName) && ['left','center','right','justify'].includes(style?.textAlign) ? style.textAlign : ''
    for (const attr of Array.from(element.attributes)) element.removeAttribute(attr.name)
    if (fontSize) style.fontSize = `${fontSize}px`
    if (align) style.textAlign = align
  }
  return template.innerHTML
}
export function requirementTextSnapshot(html: string): { text: string; html: string } {
  const safe = sanitizeRichHtml(html), holder = document.createElement('div'); holder.innerHTML = safe
  const read = (node: Node): string => {
    if (node.nodeType === 3) return node.textContent || ''
    if (node.nodeName === 'BR') return '\n'
    const text = Array.from(node.childNodes).map(read).join('')
    return /^(P|DIV|H1|H2|H3|LI|UL|OL|BLOCKQUOTE)$/.test(node.nodeName) ? text.replace(/\n*$/, '\n') : text
  }
  return { text: read(holder).replace(/\u200b/g, '').replace(/\u00a0/g, ' ').replace(/^\n+|\n+$/g, ''), html: safe || '<p><br></p>' }
}

/** Stable gap IDs keep selection and React DOM nodes intact between updates. */
export function normalizeRequirementBlocks(value: RequirementValue): RequirementBlock[] {
  const blocks = [...value.requirement_blocks]
  if (!blocks.some(block => block.kind === 'text') && value.requirement_text) {
    blocks.unshift({id:'requirement-rich-text',kind:'text',text:value.requirement_text,html:escapeRequirementHtml(value.requirement_text)})
  }
  const output: RequirementBlock[] = []
  for (const block of blocks) {
    if (block.kind !== 'text' && output.at(-1)?.kind !== 'text') output.push({id:`requirement-before-${block.id}`,kind:'text',text:'',html:'<p><br></p>'})
    output.push(block)
  }
  if (output.at(-1)?.kind !== 'text') output.push({id:`requirement-after-${output.at(-1)?.id ?? 'empty'}`,kind:'text',text:'',html:'<p><br></p>'})
  return output
}

/** Clone both sides rather than mutating the live editor before the atomic state update. */
export function splitRequirementText(editor: HTMLElement, saved?: Range | null): [{text:string;html:string},{text:string;html:string}] {
  const range = saved && editor.contains(saved.startContainer) && editor.contains(saved.endContainer) ? saved : document.createRange()
  if (range !== saved) { range.selectNodeContents(editor); range.collapse(false) }
  const before = document.createRange(), after = document.createRange()
  before.selectNodeContents(editor); before.setEnd(range.startContainer, range.startOffset)
  after.selectNodeContents(editor); after.setStart(range.endContainer, range.endOffset)
  const snapshot = (part: Range) => { const holder = document.createElement('div'); holder.append(part.cloneContents()); return requirementTextSnapshot(holder.innerHTML) }
  return [snapshot(before), snapshot(after)]
}

export function insertRequirementMedia(blocks: RequirementBlock[], textId: string, items: RequirementBlock[], parts: [{text:string;html:string},{text:string;html:string}], newId: () => string): RequirementBlock[] {
  if (!items.length) return blocks
  const index = blocks.findIndex(block => block.id === textId && block.kind === 'text')
  if (index < 0) return blocks
  const next = [...blocks]
  next.splice(index, 1, {...blocks[index],...parts[0]}, ...items, {id:newId(),kind:'text',...parts[1]})
  return next
}
